/**
 * Custom error classes.
 */

import type {
	GeneralValidationError,
	ValidationErrorsResponse,
} from 'models/api';
import type { ValueOf } from 'types';
import { filterTruthy } from 'utils/collection';

/** Error for a bad HTTP response. */
export class ResponseError extends Error {
	readonly headers: Headers;

	readonly status: number;

	readonly statusText: string;

	readonly response: unknown;

	constructor({
		headers,
		message,
		options,
		response,
		status,
		statusText,
	}: {
		headers: Headers;
		message?: string;
		options?: ErrorOptions;
		response: unknown;
		status: number;
		statusText: string;
	}) {
		super(message, options);
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, ResponseError);
		}
		this.name = 'ResponseError';

		this.headers = headers;
		this.status = status;
		this.statusText = statusText;
		this.response = response;
	}
}

/** Error for validation issues. */
export class ValidationError extends Error {
	readonly fieldErrors: Record<string, GeneralValidationError[]>;

	readonly generalErrors: Required<GeneralValidationError>[];

	constructor(
		{ errors }: ValidationErrorsResponse,
		message?: string,
		options?: ErrorOptions,
	) {
		super(message, options);
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, ValidationError);
		}
		this.name = 'ValidationError';

		const fieldErrors: typeof this.fieldErrors = {};
		const generalErrors: typeof this.generalErrors = [];
		filterTruthy(errors, 'text').forEach((error) => {
			if ('fieldName' in error && error.fieldName) {
				fieldErrors[error.fieldName] ??= [];
				fieldErrors[error.fieldName]?.push({
					key: error.key,
					text: error.text,
				});
			} else {
				// Explicitly set props to not include empty fieldName.
				generalErrors.push({ key: error.key, text: error.text });
			}
		});
		this.fieldErrors = fieldErrors;
		this.generalErrors = generalErrors;
	}
}

export const ERRORS = {
	Error,
	ResponseError,
	ValidationError,
};

export type Errors = typeof ERRORS;
export type ErrorName = keyof Errors;
export type ErrorObject = InstanceType<ValueOf<Errors>>;

/** Check if a value is an error, optionally a specific one. */
export function isError<E extends ErrorName>(
	val: unknown,
	errorName?: E,
): val is InstanceType<Errors[E]> {
	return errorName ? val instanceof ERRORS[errorName] : val instanceof Error;
}
